import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { MessageSquare, Send, AlertCircle } from 'lucide-react'
import type { RootState, AppDispatch } from '../store'
import type { CommentTargetType, ProjectMember } from '../data/projectTypes'
import {
  useGetCommentsQuery,
  useAddCommentMutation,
  useGetProjectMembersQuery,
} from '../services/projectDataApi'
import { login } from './UserMenu'
import './CommentsSection.css'

// The label a member is @mentioned by: their display name, else the local part of their email, else
// their id. Kept stable between the mention picker and the submit-time resolution so the two agree.
function memberLabel(member: ProjectMember): string {
  const name = (member.name ?? '').trim()
  if (name) return name
  const email = (member.email ?? '').trim()
  if (email.includes('@')) return email.split('@')[0] || email
  if (email) return email
  return member.user_id
}

function initials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const diffMs = Date.now() - d.getTime()
  const sec = Math.round(diffMs / 1000)
  if (sec < 60) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  if (day < 7) return `${day}d ago`
  return d.toLocaleDateString()
}

// Render comment text with @mentions of known members highlighted. Splits on each `@label` token that
// matches a current member so the mention reads as a pill; everything else is plain text.
function renderContent(content: string, memberLabels: string[]): React.ReactNode {
  if (memberLabels.length === 0) return content
  // Longest labels first so "@Jane Doe" wins over "@Jane".
  const sorted = [...memberLabels].sort((a, b) => b.length - a.length)
  const escaped = sorted.map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const re = new RegExp(`@(?:${escaped.join('|')})`, 'g')
  const out: React.ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  let key = 0
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) out.push(content.slice(last, m.index))
    out.push(
      <span key={`m-${key++}`} className="comments__mention">{m[0]}</span>,
    )
    last = m.index + m[0].length
  }
  if (last < content.length) out.push(content.slice(last))
  return out
}

export interface CommentsSectionProps {
  projectId: string
  targetType: CommentTargetType
  targetId: string
}

// The comment thread mounted at the bottom of the Bug detail and Test Run detail views. Anchored with
// id="comments" so notification deep-links (…/bugs/:id#comments) can scroll straight to it.
export default function CommentsSection({ projectId, targetType, targetId }: CommentsSectionProps) {
  const user = useSelector((state: RootState) => state.auth.user)
  const dispatch = useDispatch<AppDispatch>()
  const location = useLocation()
  const rootRef = useRef<HTMLElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const commentsQuery = useGetCommentsQuery({ projectId, targetType, targetId })
  const membersQuery = useGetProjectMembersQuery(projectId, { skip: !projectId })
  const [addComment, addState] = useAddCommentMutation()

  const comments = commentsQuery.data ?? []
  const members = useMemo(() => membersQuery.data?.items ?? [], [membersQuery.data])
  const viewerRole = membersQuery.data?.role
  const canComment = !!user && (viewerRole === 'owner' || viewerRole === 'admin' || viewerRole === 'member')
  // The role decides between the composer and the "not a member" note, and it isn't known on the first
  // render — so hold both back rather than telling a member they can't comment and then handing them a
  // textarea. The thread above already says it's loading, so this needs no spinner of its own.
  const rolePending = !!user && membersQuery.isLoading

  const memberLabels = useMemo(() => members.map(memberLabel), [members])

  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Mention autocomplete state: the query typed after the active `@`, and where that `@` starts.
  const [mention, setMention] = useState<{ query: string; start: number } | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  // Scroll to the thread when navigated to with the #comments anchor (notification deep-link).
  useEffect(() => {
    if (location.hash !== '#comments') return
    const el = rootRef.current
    if (!el) return
    const t = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
    return () => clearTimeout(t)
  }, [location.hash, commentsQuery.isLoading])

  const mentionMatches = useMemo(() => {
    if (!mention) return []
    const q = mention.query.toLowerCase()
    return members
      .filter((m) => {
        const label = memberLabel(m).toLowerCase()
        const email = (m.email ?? '').toLowerCase()
        return q === '' || label.includes(q) || email.includes(q)
      })
      .slice(0, 6)
  }, [mention, members])

  // Recompute the active mention context from the caret position: an `@` with no whitespace between
  // it and the caret opens the picker with the text after it as the query.
  const syncMention = useCallback((value: string, caret: number) => {
    const upto = value.slice(0, caret)
    const at = upto.lastIndexOf('@')
    if (at === -1) { setMention(null); return }
    const between = upto.slice(at + 1)
    if (/\s/.test(between)) { setMention(null); return }
    // Require the @ to be at the start or preceded by whitespace, so emails don't trigger it.
    const prev = at === 0 ? '' : upto[at - 1]!
    if (at !== 0 && !/\s/.test(prev)) { setMention(null); return }
    setMention({ query: between, start: at })
    setActiveIndex(0)
  }, [])

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value)
    syncMention(e.target.value, e.target.selectionStart ?? e.target.value.length)
  }

  const insertMention = useCallback((member: ProjectMember) => {
    if (!mention) return
    const label = memberLabel(member)
    const before = text.slice(0, mention.start)
    const caret = textareaRef.current?.selectionStart ?? text.length
    const after = text.slice(caret)
    const inserted = `@${label} `
    const next = before + inserted + after
    setText(next)
    setMention(null)
    // Restore focus + caret after the inserted mention.
    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (!el) return
      const pos = before.length + inserted.length
      el.focus()
      el.setSelectionRange(pos, pos)
    })
  }, [mention, text])

  const resolveMentionedUserIds = useCallback((value: string): string[] => {
    const ids: string[] = []
    for (const m of members) {
      const label = memberLabel(m)
      if (value.includes(`@${label}`)) ids.push(m.user_id)
    }
    return [...new Set(ids)]
  }, [members])

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && mentionMatches.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => (i + 1) % mentionMatches.length); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionMatches[activeIndex]!); return }
      if (e.key === 'Escape') { e.preventDefault(); setMention(null); return }
    }
    // Cmd/Ctrl+Enter submits.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void submit()
    }
  }

  const submit = useCallback(async () => {
    const content = text.trim()
    if (!content || addState.isLoading) return
    setError(null)
    try {
      await addComment({
        projectId,
        targetType,
        targetId,
        content,
        mentionedUserIds: resolveMentionedUserIds(content),
      }).unwrap()
      setText('')
      setMention(null)
    } catch (e) {
      setError(
        typeof e === 'object' && e !== null && 'status' in e && (e as { status?: unknown }).status === 401
          ? 'Please sign in to comment.'
          : 'Failed to post comment. Please try again.',
      )
    }
  }, [text, addState.isLoading, addComment, projectId, targetType, targetId, resolveMentionedUserIds])

  return (
    <section ref={rootRef} id="comments" className="comments" data-testid="comments-section" aria-label="Comments">
      <h2 className="comments__title">
        <MessageSquare size={18} strokeWidth={1.75} aria-hidden />
        Comments
        {comments.length > 0 && <span className="comments__count">{comments.length}</span>}
      </h2>

      <div className="comments__list" data-testid="comments-list">
        {commentsQuery.isLoading ? (
          <p className="comments__empty" role="status">Loading comments…</p>
        ) : comments.length === 0 ? (
          <p className="comments__empty">No comments yet.</p>
        ) : (
          comments.map((c) => {
            const label = c.author_name || c.author_email || 'Unknown'
            return (
              <article key={c.id} className="comments__item" data-testid="comment-item">
                <div className="comments__avatar" aria-hidden>{initials(label)}</div>
                <div className="comments__body">
                  <div className="comments__meta">
                    <span className="comments__author">{label}</span>
                    <span className="comments__when" title={new Date(c.created_at).toLocaleString()}>{formatWhen(c.created_at)}</span>
                  </div>
                  <div className="comments__content">{renderContent(c.content, memberLabels)}</div>
                </div>
              </article>
            )
          })
        )}
      </div>

      {rolePending ? null : canComment ? (
        <div className="comments__composer">
          <div className="comments__input-wrap">
            <textarea
              ref={textareaRef}
              className="comments__input"
              value={text}
              onChange={onChange}
              onKeyDown={onKeyDown}
              placeholder="Add a comment… use @ to mention a teammate"
              rows={3}
              data-testid="comment-input"
              aria-label="Add a comment"
            />
            {mention && mentionMatches.length > 0 && (
              <ul className="comments__mention-menu" role="listbox" data-testid="mention-menu">
                {mentionMatches.map((m, i) => (
                  <li key={m.id} role="option" aria-selected={i === activeIndex}>
                    <button
                      type="button"
                      className={`comments__mention-option ${i === activeIndex ? 'comments__mention-option--active' : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); insertMention(m) }}
                      data-testid="mention-option"
                    >
                      <span className="comments__mention-avatar" aria-hidden>{initials(memberLabel(m))}</span>
                      <span className="comments__mention-label">{memberLabel(m)}</span>
                      {m.email && <span className="comments__mention-email">{m.email}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {error && (
            <p className="comments__error" role="alert" data-testid="comment-error">
              <AlertCircle size={14} aria-hidden /> {error}
            </p>
          )}
          <div className="comments__actions">
            <button
              type="button"
              className="comments__submit"
              onClick={() => void submit()}
              disabled={!text.trim() || addState.isLoading}
              data-testid="comment-submit"
              data-analytics-event="comment-submit"
            >
              <Send size={14} aria-hidden />
              {addState.isLoading ? 'Posting…' : 'Comment'}
            </button>
          </div>
        </div>
      ) : (
        <div className="comments__signed-out" data-testid="comments-signed-out">
          {user ? (
            <p className="comments__empty">Only project members can comment.</p>
          ) : (
            <button
              type="button"
              className="comments__submit"
              onClick={() => login(dispatch, window.location.pathname + window.location.hash)}
              data-testid="comments-signin"
              data-analytics-event="comment-signin"
            >
              Sign in to comment
            </button>
          )}
        </div>
      )}
    </section>
  )
}
